import type { ReactNode } from "react";
import { useFormContext } from "react-hook-form";
import { ui } from "@patient-watch/ui";
import type { MultiSelectAddOptionActions } from "@patient-watch/ui/multi-select";
import type { OptionT } from "./Select";

function optionLabelToString(label: ReactNode): string {
    if (label === null || label === undefined) return "";
    if (typeof label === "string") return label;
    if (typeof label === "number" || typeof label === "boolean") return String(label);
    return "";
}

interface Props {
    name: string;
    label?: string;
    description?: ReactNode;
    trigger: React.ReactNode;
    options: OptionT[];
    className?: string;
    popoverContentClassName?: string;
    disabled?: boolean;
    addOption?: ReactNode | ((actions: MultiSelectAddOptionActions) => ReactNode);
    addOptionTitle?: string;
    addOptionTriggerLabel?: string;
}

const MultiSelect = ({
    name,
    trigger,
    options,
    label,
    description,
    className,
    popoverContentClassName,
    disabled,
    addOption,
    addOptionTitle,
    addOptionTriggerLabel,
}: Props) => {
    const { watch, setValue } = useFormContext();
    const selectedValues = (watch(name) as string[] | undefined) ?? [];
    const safeOptions = options ?? [];

    return (
        <div className="flex flex-col gap-4 w-full">
            {(label || description) && (
                <div className="flex flex-col gap-1">
                    {label && <ui.Label className="font-medium text-sm">{label}</ui.Label>}
                    {description && <span className="text-slate-500 text-xs">{description}</span>}
                </div>
            )}
            <ui.MultiSelect
                options={safeOptions.map((o) => ({
                    label: optionLabelToString(o.label) || o.searchText || o.value,
                    value: o.value,
                    description: o.description,
                    searchText: o.searchText,
                    icon: o.icon,
                }))}
                value={selectedValues}
                onValueChange={(next: string[]) => setValue(name, next)}
                placeholder={typeof trigger === "string" ? trigger : "Select options"}
                className={className}
                popoverContentClassName={popoverContentClassName}
                disabled={disabled}
                addOption={addOption}
                addOptionTitle={addOptionTitle}
                addOptionTriggerLabel={addOptionTriggerLabel}
            />
        </div>
    );
};

export default MultiSelect;
